/** Food waste lifecycle tab - funnel visualisation from purchased through to final
 * disposition (wasted / donated / recycled / composted / anaerobic digestion / landfill),
 * showing where losses occur along the chain. */

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { LIFECYCLE_STAGES } from '../data';

const FUNNEL_STAGES = ['Purchased', 'Received', 'Stored', 'Prepared', 'Produced', 'Sold'];
const DISPOSITION_STAGES = ['Wasted', 'Donated', 'Recycled', 'Composted', 'Anaerobic digestion', 'Landfill'];

const LifecycleTab = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const funnel = FUNNEL_STAGES.map((stage) => LIFECYCLE_STAGES.find((s) => s.stage === stage)).filter(Boolean) as typeof LIFECYCLE_STAGES;
  const maxQty = funnel[0]?.quantityKg ?? 1;
  const disposition = DISPOSITION_STAGES.map((stage) => LIFECYCLE_STAGES.find((s) => s.stage === stage)).filter(Boolean) as typeof LIFECYCLE_STAGES;
  const wastedQty = LIFECYCLE_STAGES.find((s) => s.stage === 'Wasted')?.quantityKg ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 1 }}>
            {t('wasteDashboardLifecycleTitle', 'Food waste lifecycle')}
          </Typography>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 3, display: 'block' }}>
            {t('wasteDashboardLifecycleSubtitle', 'Where losses occur between purchasing and final sale.')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {funnel.map((stage, idx) => {
              const widthPercent = Math.max(8, (stage.quantityKg / maxQty) * 100);
              const priorQty = idx > 0 ? funnel[idx - 1].quantityKg : stage.quantityKg;
              const lossAtStage = priorQty - stage.quantityKg;
              return (
                <Box key={stage.stage}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: 110, flexShrink: 0 }}>
                    <Typography variant="textXS">{stage.stage}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{
                      width: `${widthPercent}%`,
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      borderRadius: '6px',
                      px: 2,
                      py: 1,
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}>
                      {stage.quantityKg.toLocaleString()} kg
                    </Box>
                    {idx > 0 && lossAtStage > 0 && (
                      <Typography variant="textXS"
                        color="error.main"
                        sx={{ ml: 2 }}>
                        {t('wasteDashboardLossAtStage', 'Loss at this stage')}: {lossAtStage.toLocaleString()} kg
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardDispositionTitle', 'Where wasted food ends up')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardDisposition', 'Disposition')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteQuantity', 'Quantity (kg)')}</TableCell>
                <TableCell align="right">{t('wasteDashboardShareOfWasted', 'Share of wasted')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {disposition.filter((d) => d.stage !== 'Wasted').map((d) => (
                <TableRow key={d.stage}>
                  <TableCell>{d.stage}</TableCell>
                  <TableCell align="right">{d.quantityKg.toLocaleString()}</TableCell>
                  <TableCell align="right">{wastedQty ? ((d.quantityKg / wastedQty) * 100).toFixed(1) : '0.0'}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </Box>
  );
};

export default LifecycleTab;
